import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LangContext";
import LangSwitch from "@/components/LangSwitch";
import Logo from "@/components/Logo";
import { ArrowRight, CheckCircle, Globe, FileText, Lightning, Palette, Translate, ChartBar, ShieldCheck, Leaf, WhatsappLogo, Headset } from "@phosphor-icons/react";
import UncleDaveWidget from "@/components/UncleDaveWidget";
import WelcomePopup from "@/components/WelcomePopup";
import { TutorialVideoCard, TutorialVideoModal } from "@/components/TutorialVideo";

const HERO_IMG = "/hero1_new.webp";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const { t, isRtl } = useLang();
  const [email, setEmail] = useState("");
  const [registered, setRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [interestError, setInterestError] = useState("");
  const [activeSection, setActiveSection] = useState("home");
  const [tutorialOpen, setTutorialOpen] = useState(false);

  const handleRegisterInterest = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setInterestError("");
    try {
      await axios.post(`${API}/interest`, { email });
      setRegistered(true);
    } catch (err) {
      setInterestError(err?.response?.data?.detail || t('interest_error_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#070709] text-white">
      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#070709] border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex-shrink-0">
            <Logo size="md" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            {[
              { id: "home", label: t('nav_home') },
              { id: "disrupting", label: t('nav_why_us') },
              { id: "features", label: t('nav_features') },
              { id: "pricing", label: t('nav_pricing') },
            ].map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)} data-testid={`nav-${item.id}`}
                className={`font-['Outfit'] text-sm font-medium transition-colors ${
                  activeSection === item.id
                    ? "text-[#01AF9B]"
                    : item.id === "pricing"
                      ? "text-[#EAB308] hover:text-[#FACC15] font-semibold"
                      : "text-slate-400 hover:text-white"
                }`}>
                {item.label}
              </button>
            ))}
            <button onClick={() => navigate("/founders")} data-testid="nav-founders"
              className="font-['Outfit'] text-sm font-medium text-slate-400 hover:text-white transition-colors">
              {t('nav_founders')}
            </button>
            <button onClick={() => navigate("/sample")} data-testid="nav-sample"
              className="font-['Outfit'] text-sm font-medium text-slate-400 hover:text-[#01AF9B] transition-colors">
              {t('nav_sample')}
            </button>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Mobile-only Pricing link, desktop nav is hidden below md.
                Keeps the yellow attention-grabber visible on phones. */}
            <button onClick={() => scrollTo("pricing")} data-testid="nav-pricing-mobile"
              className="md:hidden font-['Outfit'] text-xs font-bold text-[#EAB308] hover:text-[#FACC15] transition-colors px-2">
              {t('nav_pricing')}
            </button>
            <LangSwitch />
            <button onClick={() => navigate("/login")} data-testid="nav-login"
              className="font-['Outfit'] text-xs sm:text-sm font-semibold text-slate-300 hover:text-white transition-colors">
              {t('nav_sign_in')}
            </button>
            <button onClick={() => navigate("/register")} data-testid="nav-register"
              className="font-['Outfit'] text-[13px] sm:text-sm font-bold bg-[#01AF9B] hover:bg-[#018F7E] text-white px-3.5 sm:px-4 py-2 rounded-lg transition-all ring-2 ring-[#01AF9B]/40 shadow-[0_4px_14px_rgba(1,175,155,0.35)] whitespace-nowrap">
              {t('nav_sign_up')}
            </button>
          </div>
        </div>
      </nav>

      {/* HERO, mobile uses a stacked layout (photo top, text bottom on
          solid dark bg) so the girl's face is fully visible without
          overlapping the text. Desktop (md+) keeps the original overlay. */}
      <section id="home" className="relative bg-[#070709] md:min-h-[720px]">
        {/* Mobile-only photo band, 360px tall, object-cover so the girl's
            face is prominent but no longer stretched. Hidden on md+. */}
        <div className="md:hidden relative w-full h-[360px] overflow-hidden">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover object-[center_35%]" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[#070709]" />
        </div>
        {/* Desktop-only full-bleed photo background, pinned to viewport
            height (NOT the section height) so it keeps its exact original
            crop/zoom even if the section grows with longer content. */}
        <div className="hidden md:block absolute top-0 left-0 right-0 h-screen min-h-[720px] z-0 overflow-hidden">
          <img src={HERO_IMG} alt=""
               className="w-full h-full object-cover object-[center_top] bg-[#070709]" />
          <div className={`absolute inset-0 ${isRtl ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-[#070709]/60 via-[#070709]/15 to-transparent`} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 pt-8 sm:pt-36 lg:pt-44 pb-10 sm:pb-14">
          <div className="animate-fade-in-up max-w-2xl">
            {/* Spacer preserving the original hero vertical rhythm after
                the "FREE SIGN-UP" badge was moved into the WelcomePopup.
                Same box + margin as the removed badge, `invisible` so it
                takes space but renders nothing. */}
            <div className="inline-flex items-center gap-2 border border-transparent rounded-full px-4 py-1.5 mb-5 sm:mb-6 invisible" aria-hidden="true">
              <Leaf size={14} />
              <span className="font-['Outfit'] text-xs font-semibold tracking-wide">·</span>
            </div>
            <h1 className="font-['Outfit'] text-3xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5 sm:mb-6 tracking-tight">
              {t('hero_title_a')}<br/>
              <span className="text-white">{t('hero_title_b')}</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-300 max-w-lg leading-relaxed">
              {t('hero_desc')}
            </p>
          </div>
        </div>
      </section>

      {/* HERO SUB-BAND: 2-column CTA grid placed BELOW the hero photo on a
          solid dark band. Left column holds the primary CTAs (video card +
          Get Started + Subscribe). Right column holds secondary CTAs and
          the standards-pills row. Columns stay vertically aligned on their
          top edge so the composition reads cleanly. */}
      <section className="relative bg-[#070709] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-8 sm:pt-10 pb-4 sm:pb-5 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-8 items-start">
          {/* LEFT: Primary CTA cluster */}
          <div className="flex flex-col gap-3 sm:gap-4 w-full max-w-[460px]">
            <TutorialVideoCard onOpen={() => setTutorialOpen(true)} />
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button onClick={() => navigate("/register")} data-testid="hero-cta-signup"
                className="flex-1 bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-bold text-base sm:text-lg py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all flex items-center gap-2 justify-center whitespace-nowrap shadow-[0_6px_20px_rgba(1,175,155,0.35)] ring-1 ring-[#01AF9B]/60">
                {t('hero_cta_start')} <ArrowRight size={20} className={isRtl ? "rotate-180" : ""} />
              </button>
              <button onClick={() => navigate("/subscribe")} data-testid="hero-cta-subscribe"
                className="flex-1 bg-[#EAB308] hover:bg-[#FACC15] text-[#0B0F1A] font-['Outfit'] font-bold text-base sm:text-lg py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all flex items-center gap-2 justify-center whitespace-nowrap shadow-[0_6px_20px_rgba(234,179,8,0.30)]">
                {t('hero_cta_subscribe')} <ArrowRight size={20} className={isRtl ? "rotate-180" : ""} />
              </button>
            </div>
          </div>
          {/* RIGHT: Secondary CTAs + standards pills */}
          <div className="flex flex-col gap-5 sm:gap-6 w-full lg:w-fit">
            <div className="grid grid-cols-6 gap-3 sm:gap-4 lg:flex lg:flex-wrap">
              <button onClick={() => navigate("/founders")} data-testid="hero-founders-btn"
                className="col-span-2 lg:hidden border border-white/20 hover:border-white/40 text-white font-['Outfit'] font-semibold text-sm sm:text-base py-3 sm:py-4 px-3 sm:px-7 rounded-xl transition-all text-center whitespace-nowrap">
                {t('nav_founders')}
              </button>
              <button onClick={() => scrollTo("disrupting")}
                className="col-span-2 lg:col-auto border border-white/20 hover:border-white/40 text-white font-['Outfit'] font-semibold text-sm sm:text-base py-3 sm:py-4 px-3 sm:px-7 rounded-xl transition-all text-center whitespace-nowrap">
                {t('hero_cta_learn')}
              </button>
              <button onClick={() => navigate("/sample")} data-testid="hero-sample-btn"
                className="col-span-2 lg:hidden border border-[#01AF9B]/40 hover:border-[#01AF9B] bg-[#01AF9B]/5 hover:bg-[#01AF9B]/15 text-[#01AF9B] font-['Outfit'] font-semibold text-sm sm:text-base py-3 sm:py-4 px-3 sm:px-7 rounded-xl transition-all text-center whitespace-nowrap">
                {t('hero_cta_sample')}
              </button>
              <a
                href="/downloads/required-assumptions.zip"
                download
                data-testid="hero-assumptions-btn"
                className="col-span-3 lg:col-auto border border-white/20 hover:border-white/40 text-white font-['Outfit'] font-semibold text-sm sm:text-base py-3 sm:py-4 px-3 sm:px-7 rounded-xl transition-all text-center whitespace-nowrap">
                {t('hero_cta_assumptions')}
              </a>
              <button onClick={() => navigate("/professionals")} data-testid="hero-advisors-btn"
                className="col-span-3 lg:flex-1 bg-[#F97316] hover:bg-[#EA630C] text-white font-['Outfit'] font-bold text-sm sm:text-base py-3 sm:py-4 px-3 sm:px-7 rounded-xl transition-all text-center whitespace-nowrap shadow-[0_6px_20px_rgba(249,115,22,0.35)]">
                {t('nav_advisors')}
              </button>
            </div>
            <div className="flex flex-nowrap items-center justify-between gap-1 sm:gap-2 w-full overflow-x-auto lg:overflow-visible" data-testid="hero-standards-pills">
              {[t('badge_gri'), t('badge_tcfd'), t('badge_adx'), t('badge_dfm'), t('badge_sdg'), t('badge_issb'), t('badge_csrd')].map((label, i) => (
                <span key={i} className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-[#01AF9B]/10 border border-[#01AF9B]/30 text-slate-200 font-['Outfit'] font-medium text-[10px] sm:text-xs whitespace-nowrap flex-shrink-0">
                  <CheckCircle size={12} weight="fill" className="text-[#01AF9B] flex-shrink-0 hidden sm:block" /> {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DISRUPTING */}
      <section id="disrupting" className="pt-6 sm:pt-10 pb-16 sm:pb-24 px-5 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <div className="font-['Outfit'] text-xs font-bold tracking-[0.3em] uppercase text-[#01AF9B] mb-3">{t('why_eyebrow')}</div>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight">
              {t('why_title_a')}<br/>{t('why_title_b')}
            </h2>
            <div className="w-16 h-1 bg-[#01AF9B] mx-auto rounded-full mb-8"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                <strong className="text-white">{t('why_p1_strong')}</strong>{t('why_p1_rest')}
              </p>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                <strong className="text-white">{t('why_p2_strong')}</strong>{t('why_p2_rest')}<strong className="text-[#01AF9B]">{t('why_p2_highlight')}</strong>{t('why_p2_tail')}
              </p>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                <strong className="text-white">{t('why_p3_strong')}</strong>{t('why_p3_rest')}
              </p>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                {t('why_p4_pre')}<strong className="text-white">{t('why_p4_strong')}</strong>{t('why_p4_rest')}
              </p>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                {t('why_p5_pre')}<strong className="text-white">{t('why_p5_strong')}</strong>{t('why_p5_mid')}<strong className="text-white">{t('why_p5_strong2')}</strong>{t('why_p5_rest')}
              </p>
            </div>
            <div className="space-y-4">
              <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-6 hover:border-[#01AF9B]/30 transition-all">
                <div className="font-['Outfit'] text-4xl font-bold text-[#01AF9B] mb-2">{t('stat1_value')}</div>
                <div className="font-['Outfit'] text-sm font-semibold text-white mb-1">{t('stat1_title')}</div>
                <p className="text-sm text-slate-400">{t('stat1_desc')}</p>
              </div>
              <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-6 hover:border-[#8B5CF6]/30 transition-all">
                <div className="font-['Outfit'] text-4xl font-bold text-[#8B5CF6] mb-2">{t('stat2_value')}</div>
                <div className="font-['Outfit'] text-sm font-semibold text-white mb-1">{t('stat2_title')}</div>
                <p className="text-sm text-slate-400">{t('stat2_desc')}</p>
              </div>
              <div className="bg-[#111216] border border-slate-800/60 rounded-2xl p-6 hover:border-[#F97316]/30 transition-all">
                <div className="font-['Outfit'] text-4xl font-bold text-[#F97316] mb-2">{t('stat3_value')}</div>
                <div className="font-['Outfit'] text-sm font-semibold text-white mb-1">{t('stat3_title')}</div>
                <p className="text-sm text-slate-400">{t('stat3_desc')}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-10">
            <img src="/hero2.jpg" alt="Team planning ESG strategy" className="rounded-xl border border-white/10 w-full h-[420px] object-cover" />
            <img src="/hero4.jpg" alt="ESG data analysis" className="rounded-xl border border-white/10 w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-16 sm:py-24 px-5 sm:px-6 bg-[#0A0D12]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="font-['Outfit'] text-xs font-bold tracking-[0.3em] uppercase text-[#01AF9B] mb-4">{t('features_eyebrow')}</div>
            <h2 className="font-['Outfit'] text-4xl font-bold mb-4 tracking-tight">{t('features_title')}</h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">{t('features_subtitle')}</p>
          </div>
          <div className="mb-12 rounded-2xl overflow-hidden border border-white/10 max-h-[360px]">
            <img src="/hero3.jpg" alt="Business team collaboration" className="w-full object-cover" style={{ maxHeight: "360px" }} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Lightning size={24} />, title: t('feat_ai_title'), desc: t('feat_ai_desc'), color: "#01AF9B" },
              { icon: <FileText size={24} />, title: t('feat_pdf_title'), desc: t('feat_pdf_desc'), color: "#01AF9B" },
              { icon: <Translate size={24} />, title: t('feat_bilingual_title'), desc: t('feat_bilingual_desc'), color: "#8B5CF6" },
              { icon: <ChartBar size={24} />, title: t('feat_co2_title'), desc: t('feat_co2_desc'), color: "#8B5CF6" },
              { icon: <Palette size={24} />, title: t('feat_brand_title'), desc: t('feat_brand_desc'), color: "#F97316" },
              { icon: <ShieldCheck size={24} />, title: t('feat_standards_title'), desc: t('feat_standards_desc'), color: "#F97316" },
              { icon: <Globe size={24} />, title: t('feat_bench_title'), desc: t('feat_bench_desc'), color: "#01AF9B" },
              { icon: <ChartBar size={24} />, title: t('feat_matrix_title'), desc: t('feat_matrix_desc'), color: "#8B5CF6" },
              { icon: <FileText size={24} />, title: t('feat_stake_title'), desc: t('feat_stake_desc'), color: "#F97316" },
            ].map((f, i) => (
              <div key={i} className="bg-[#111216] border border-slate-800/60 rounded-xl p-6 hover:border-slate-700 transition-all group">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${f.color}15`, border: `1px solid ${f.color}30` }}>
                  <span style={{ color: f.color }}>{f.icon}</span>
                </div>
                <h3 className="font-['Outfit'] text-base font-bold text-white mb-2 group-hover:text-[#01AF9B] transition-colors">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREATOR section (David bio) removed 2026-08-09, replaced by
          the dedicated /founders page that lists all three partners
          (David, Ali, Jallel). Reached via the "Founders" nav link. */}

      {/* TRUST / DATA INTEGRITY, 6-layer defence stack. Placed between the
          founder section and pricing so the visitor sees "why this platform
          is trustworthy" right before the price. Every card echoes the exact
          layer implemented in the codebase (see pdf_generator.py hammer and
          server.py _apply_global_scrubs). */}
      <section id="trust" className="py-16 sm:py-24 px-5 sm:px-6 bg-[#0A0D12]" data-testid="trust-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#73324B]/15 border border-[#73324B]/40 mb-5" data-testid="trust-eyebrow">
              <ShieldCheck size={16} weight="fill" className="text-[#E8B4C4]" />
              <span className="font-['Outfit'] text-xs font-bold tracking-[0.24em] uppercase text-[#E8B4C4]">{t('trust_eyebrow')}</span>
            </div>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold mb-5 tracking-tight" data-testid="trust-title">
              {t('trust_title_a')}<br/>
              <span className="text-[#01AF9B]">{t('trust_title_b')}</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto" data-testid="trust-desc">
              {t('trust_desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="trust-grid">
            {[
              { n: "01", title: t('trust_l1_title'), desc: t('trust_l1_desc') },
              { n: "02", title: t('trust_l2_title'), desc: t('trust_l2_desc') },
              { n: "03", title: t('trust_l3_title'), desc: t('trust_l3_desc') },
              { n: "04", title: t('trust_l4_title'), desc: t('trust_l4_desc') },
              { n: "05", title: t('trust_l5_title'), desc: t('trust_l5_desc') },
              { n: "06", title: t('trust_l6_title'), desc: t('trust_l6_desc') },
            ].map((layer, i) => (
              <div key={i} className="group relative bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 rounded-xl p-5 sm:p-6 hover:border-[#01AF9B]/40 hover:from-[#01AF9B]/[0.06] transition-all duration-300" data-testid={`trust-layer-${i+1}`}>
                <div className={`font-['Outfit'] text-2xl sm:text-3xl font-extralight text-[#01AF9B]/70 mb-3 tracking-tight ${isRtl ? 'text-right' : ''}`}>
                  {layer.n}
                </div>
                <div className={`font-['Outfit'] text-base sm:text-lg font-bold text-white mb-2 leading-tight ${isRtl ? 'text-right' : ''}`}>
                  {layer.title}
                </div>
                <div className={`text-sm text-slate-400 leading-relaxed ${isRtl ? 'text-right' : ''}`}>
                  {layer.desc}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center" data-testid="trust-footer">
            <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10">
              <FileText size={14} weight="fill" className="text-slate-400" />
              <span className="text-xs sm:text-sm text-slate-400 font-medium">
                {t('trust_footer')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING / GET STARTED */}
      <section id="pricing" className="py-16 sm:py-24 px-5 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="font-['Outfit'] text-xs font-bold tracking-[0.3em] uppercase text-[#01AF9B] mb-4">{t('pricing_eyebrow')}</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight">
            {t('pricing_title_a')} <span className="text-[#01AF9B]">{t('pricing_title_b')}</span>
          </h2>

          <div className="bg-[#111216] border border-[#01AF9B]/30 rounded-2xl mb-8 relative overflow-hidden">
            <div className="w-full bg-[#F97316] text-white font-['Outfit'] text-sm sm:text-base font-bold tracking-wide px-6 py-3 flex items-center justify-center gap-2 uppercase" data-testid="discount-banner">
              <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
              {t('pricing_discount_banner')}
              <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
            </div>
            <div className="p-8">
            <div className="mb-6 mt-2 text-center">
              <span className="font-['Outfit'] text-5xl font-bold text-white">{t('pricing_price')}</span>
              <span className="text-slate-400 text-lg">{t('pricing_per_year')}</span>
              <div className="text-base sm:text-lg font-bold text-[#F97316] mt-2" data-testid="pricing-discount-note"><span className="line-through text-slate-500 font-normal">{t('pricing_strike')}</span>{t('pricing_discount_note')}</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left mb-8 max-w-md mx-auto">
              {[
                t('feature_unlimited'),
                t('feature_pdf'),
                t('feature_bilingual'),
                t('feature_ai_content'),
                t('feature_branding'),
                t('feature_standards'),
                t('feature_uploads'),
                t('feature_toolkit'),
                t('feature_benchmarks'),
                t('feature_matrix'),
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                  <CheckCircle size={16} weight="fill" className="text-[#01AF9B] flex-shrink-0" /> {f}
                </div>
              ))}
            </div>

            {/* ── HUMAN-POWERED assistance callout ─────────────────
                Emphasises that the plan includes founder-level human
                onboarding and real-time guidance on the first report.
                Placed above the "Live Now" panel so users read it
                right after the feature list. */}
            <div className="relative rounded-2xl p-[1.5px] mb-6 bg-gradient-to-br from-[#F97316] via-[#B94B05] to-[#01AF9B]"
                 data-testid="pricing-human-assistance">
              <div className="bg-[#0F1013] rounded-2xl px-6 py-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 bg-[#F97316]/15 border border-[#F97316]/40 text-[#F97316] text-[10px] tracking-[0.25em] uppercase font-bold px-2.5 py-1 rounded-full">
                    <Lightning size={12} weight="fill" />
                    {t('pricing_human_badge')}
                  </span>
                  <span className="font-['Outfit'] text-white text-base font-bold">
                    {t('pricing_human_title')}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                  <div className="flex gap-3" data-testid="pricing-human-hook-1">
                    <div className="w-9 h-9 rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center flex-shrink-0">
                      <WhatsappLogo size={18} weight="fill" className="text-[#25D366]" />
                    </div>
                    <div>
                      <div className="font-['Outfit'] text-sm font-bold text-white mb-1 flex items-center gap-1.5">
                        <CheckCircle size={14} weight="fill" className="text-[#01AF9B]" />
                        {t('pricing_human_1_title')}
                      </div>
                      <p className="text-xs text-slate-400 leading-relaxed">{t('pricing_human_1_desc')}</p>
                    </div>
                  </div>

                  <div className="flex gap-3" data-testid="pricing-human-hook-2">
                    <div className="w-9 h-9 rounded-lg bg-[#01AF9B]/15 border border-[#01AF9B]/40 flex items-center justify-center flex-shrink-0">
                      <Headset size={18} weight="fill" className="text-[#01AF9B]" />
                    </div>
                    <div>
                      <div className="font-['Outfit'] text-sm font-bold text-white mb-1 flex items-center gap-1.5">
                        <CheckCircle size={14} weight="fill" className="text-[#01AF9B]" />
                        {t('pricing_human_2_title')}
                      </div>
                      <p className="text-xs text-slate-400 leading-relaxed">{t('pricing_human_2_desc')}</p>
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-slate-500 italic mt-4 pt-3 border-t border-slate-800/60" data-testid="pricing-human-footnote">
                  {t('pricing_human_footnote')}
                </p>
              </div>
            </div>

            <div className="bg-[#01AF9B]/10 border border-[#01AF9B]/20 rounded-xl p-5 mb-6">
              <div className="font-['Outfit'] text-sm font-bold text-[#01AF9B] mb-2">{t('pricing_soon_title')}</div>
              <p className="text-sm text-slate-300">{t('pricing_soon_desc')}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="pricing-cta-row">
              <button onClick={() => navigate("/register")} data-testid="pricing-cta-register"
                className="flex-1 bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-bold py-3 px-6 rounded-lg transition-all flex items-center gap-2 justify-center whitespace-nowrap">
                {t('hero_cta_start')} <ArrowRight size={18} className={isRtl ? "rotate-180" : ""} />
              </button>
              <button onClick={() => navigate("/sample")} data-testid="pricing-cta-sample"
                className="flex-1 border border-[#01AF9B]/40 hover:border-[#01AF9B] bg-[#01AF9B]/5 hover:bg-[#01AF9B]/15 text-[#01AF9B] font-['Outfit'] font-semibold py-3 px-6 rounded-lg transition-all whitespace-nowrap">
                {t('pricing_cta_sample_first')} →
              </button>
            </div>
            {/* Risk-reversal line, reduces hesitation before annual
                commitment. Matches user's flow: users can preview
                their full report before paying. */}
            <p className="mt-4 text-xs text-slate-400 font-['Outfit'] flex items-center gap-1.5 justify-center" data-testid="pricing-risk-reversal">
              <CheckCircle size={12} weight="fill" className="text-[#01AF9B] flex-shrink-0" />
              {t('pricing_risk_reversal')}
            </p>
            </div>
          </div>

          <p className="text-slate-500 text-sm">
            {t('have_account_pre')}<button onClick={() => navigate("/login")} className="text-[#01AF9B] hover:underline font-medium">{t('have_account_link')}</button>
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/[0.06] py-8 sm:py-12 px-5 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-4 sm:flex-row sm:justify-between sm:gap-6">
          <Logo size="sm" />
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 text-sm text-slate-500">
            <a href="mailto:info@architaly.green" className="hover:text-[#01AF9B] transition-colors">info@architaly.green</a>
            <span>{t('footer_location')}</span>
            <button onClick={() => navigate("/terms")} className="hover:text-[#01AF9B] transition-colors" data-testid="footer-terms">{t('footer_terms')}</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-[#01AF9B] transition-colors" data-testid="footer-privacy">{t('footer_privacy')}</button>
            <button onClick={() => navigate("/refund")} className="hover:text-[#01AF9B] transition-colors" data-testid="footer-refund">{t('footer_refund')}</button>
          </div>
          <p className="text-xs text-slate-600">{t('footer_copy')}</p>
        </div>
      </footer>

      <UncleDaveWidget />
      <WelcomePopup />
      <TutorialVideoModal open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
    </div>
  );
}
